import React from 'react';
import Slider, { CustomArrowProps } from 'react-slick';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { get } from 'lodash';

import { BackwardsLeftIcon, ForwardRightIcon } from '../Icons';
import { selectFeedMarker } from '../../../actions/feed';
import { fetchListingById } from '../../../actions/listing';
import FavoriteButton from '../FavoriteButton';
import DeleteButton from '../DeleteButton';
import OpenHouse from '../OpenHouse';
import {
  addressString,
  formatListingDetails,
  formatUrlAddress,
  normalizePrice,
} from '../../../lib/helpers';

import style from '../../../styles/sass/components/ListingPreview.module.scss';
import { isMobile } from '../../../lib/getDeviceSize';

interface Props {
  index?: any;
  listing: any;
  listingList: Function;
  showDelete: boolean;
  alreadyDeleted?: boolean;
  isLoved?: boolean;
  showInModal?: boolean;
  dispatch: any;
  pointDialogToSidebar?: boolean;
}

const useStyles = makeStyles({
  addressStyle: {
    color: '#0E1217',
    fontSize: 1,
    letterSpacing: 0.56,
    margin: 0,
  },
  contentStyle: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    padding: '0 10px',
    marginTop: 8,
  },
  imageContainer: {
    borderBottomColor: 'rgba(0,0,0,0.07)',
    borderBottomWidth: 1,
    height: isMobile() ? 180 : 230,
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
  },
  imageStyle: {
    background: `repeating-linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.8)), center center / cover no-repeat`,
    objectFit: 'cover',
  },
  listingDetails: {
    color: '#323232',
    fontSize: 14,
    letterSpacing: 0.44,
    margin: '5px 0 0',
  },
  listingImage: {
    flex: 1,
    height: 'auto',
    resizeMode: 'cover',
    width: 'auto',
  },
  savingsAndOpenHouseContainer: {
    bottom: 6,
    display: 'flex',
    flexDirection: 'row',
    marginLeft: 5,
    position: 'absolute',
  },

  topLeft: {
    left: 15,
    position: 'absolute',
    top: 15,
    zIndex: 6,
  },
  topRight: {
    position: 'absolute',
    right: 15,
    top: 15,
  },
  trashIcon: {
    borderRadius: 50,
    height: 24,
    minWidth: 25,
    padding: 0,
  },
  webListings: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: 8,
  },
});

const LeftIcon = ({
  currentSlide,
  slideCount,
  ...props
} = CustomArrowProps) => {
  return (
    <div
      {...props}
      onClick={e => {
        e.stopPropagation();
        props.onClick();
      }}
    >
      <BackwardsLeftIcon />
    </div>
  );
};

const RightIcon = ({
  currentSlide,
  slideCount,
  ...props
} = CustomArrowProps) => {
  return (
    <div
      {...props}
      onClick={e => {
        e.stopPropagation();
        props.onClick();
      }}
    >
      <ForwardRightIcon />
    </div>
  );
};

const settings = {
  infinite: true,
  initialSlide: 0,
  lazyLoad: 'progressive',
  nextArrow: <RightIcon />,
  prevArrow: <LeftIcon />,
  slidesToScroll: 1,
  slidesToShow: 1,
  speed: 0,
  swipeToSlide: true,
};

export function ListingsComponent(props: Props) {
  const { index, listingList, listing, dispatch } = props;
  const classes = useStyles();
  const router = useRouter();

  const handleMarkerSelect = (listingId: string, listing: any) => {
    dispatch(selectFeedMarker(listingId, listing));
  };

  const handleOpenModal = (e: any) => {
    const filters = JSON.parse(get(router, 'query.filters', '{}'));
    const photosByRoom = get(filters, 'photosByRoom', undefined);
    if (e.metaKey || e.ctrlKey || e.altKey) {
      window.open(
        `/listing/r/${listing.id}/${formatUrlAddress(listing)}${
          photosByRoom ? `?photosByRoom=${photosByRoom}` : ''
        }`,
      );
      return;
    }

    dispatch(fetchListingById(listing.listingMlsId, true, photosByRoom, true));
  };

  const renderContents = () => {
    return (
      <div style={{ textDecoration: 'none', cursor: 'pointer' }}>
        <div className={classes.imageContainer}>
          <div className={`${style.simpleSliderWrapper} simple-slider-wrapper`}>
            <div className={style.slideOverlay}>&nbsp;</div>
            <Slider {...settings}>{listingList()}</Slider>
          </div>
          {props.showDelete && (
            <div className={classes.topLeft}>
              <DeleteButton
                listing={listing}
                index={index}
                alreadyDeleted={props.alreadyDeleted}
                isLoved={props.isLoved}
              />
            </div>
          )}
          <div
            className={classes.topRight}
            onClick={(event: any) => event.preventDefault()}
          >
            <FavoriteButton
              listingId={listing.id}
              light={false}
              containerStyle={{
                top: '0px',
                right: '0px',
                padding: '0px',
              }}
              grey
              pointToSidebar={props.pointDialogToSidebar}
            />
          </div>
          <div className={classes.savingsAndOpenHouseContainer}>
            <OpenHouse listing={listing} />
          </div>
        </div>
        <div className={classes.contentStyle}>
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <h2
              className={style.priceTextStyle}
              style={{ fontFamily: 'Sharp Grotesk Semibold !important' }}
            >
              ${normalizePrice(listing.listPrice)}
            </h2>
            <p className={style.listingDetails}>
              {formatListingDetails(listing)}
            </p>
          </div>
          <p className={style.addressTextStyle}>{addressString(listing)}</p>
        </div>
      </div>
    );
  };

  return (
    <div
      className={style.listingsComponent}
      key={index}
      onMouseEnter={() => handleMarkerSelect(listing.id, listing)}
      onMouseLeave={() => handleMarkerSelect('', '')}
    >
      {props.showInModal ? (
        <div onClick={handleOpenModal}>{renderContents()}</div>
      ) : (
        <Link href={`/listing/r/${listing.id}/${formatUrlAddress(listing)}`}>
          {renderContents()}
        </Link>
      )}
    </div>
  );
}

const mapDispatchToProps = (dispatch: any) => ({
  dispatch,
});

export default connect(null, mapDispatchToProps)(ListingsComponent);
